import React from 'react';
import { isTablet } from 'react-device-detect';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import theme from 'shared/assets/style/theme';
import { BUTTON_STROKES } from 'shared/utils/buttonTextStrokes';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import GradientLabel from './components/GradientLabel';
import { isIPadMini, pwaStyleAdapter } from '../../utils/mobileSupported';

/**
 * A customizable Button component.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} [props.containerStyle={}] - Custom style object for the button container.
 * @param {Object} [props.imageStyle={}] - Custom style object for the button image.
 * @param {string} props.label - Text label displayed on the button.
 * @param {string} [props.imageSource] - Source URL for the background image. Defaults to a rounded rectangle asset.
 * @param {Function} props.onClick - Function to call on button click.
 * @param {string} [props.objectFit] - CSS object-fit property for the image. Defaults to "contain".
 * @param {Object} [props.textStyle] - Custom style object for the button text.
 * @param {boolean} [props.disabled=false] - If true, disables the button click.
 * @param {boolean} [props.isDebounce=false] - If true, debounces the onClick handler.
 * @param {string} [props.textStroke] - Color for the text stroke. Uses a default stroke if not provided.
 * @param {string} [props.buttonIcon] - Source URL for an optional button icon.
 * @param {Object} [props.buttonIconStyle] - Custom style object for the button icon.
 * @param {Object} [props.buttonWrapperStyle] - Custom style object for the content wrapper.
 * @param {React.ReactNode} [props.children] - Optional children to be rendered inside the button.
 * @param {boolean} [props.gradientLabel=false] - If true, renders the label with a gradient effect.
 * @param {Object} [props.gradentLabelTextStyle={}] - Custom style object for the gradient label text.
 * @param {string} [props.display='flex'] - CSS display property for the button container.
 *
 * @returns {JSX.Element} The rendered Button component.
 */
const Button = props => {
  const {
    containerStyle = {},
    imageStyle = {},
    label,
    imageSource,
    onClick,
    objectFit,
    textStyle,
    disabled,
    isDebounce,
    textStroke,
    buttonIcon,
    buttonIconStyle,
    buttonWrapperStyle,
    children,
    gradientLabel = false,
    gradentLabelTextStyle = {},
    display = 'flex',
    opacity = 1,
  } = props;

  const onClickHandler = debounce(() => onClick(), isDebounce ? 500 : 0, { leading: true, trailing: false });

  const strokeColor = disabled ? BUTTON_STROKES.DISABLED : textStroke || BUTTON_STROKES.DISABLED;

  return (
    <ButtonWrapper
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();
        return !disabled ? onClickHandler() : null;
      }}
      containerStyle={containerStyle}
      display={display}
      opacity={opacity}
    >
      <ButtonImage
        imageSource={imageSource ?? assetSource(ASSET_KEY.ROUNDED_RECT_PRIMARY)}
        imageStyle={imageStyle}
        objectFit={objectFit}
      />
      <ButtonContentWrapper style={buttonWrapperStyle} hasChildren={!!children}>
        {buttonIcon && <ButtonIcon src={buttonIcon} buttonIconStyle={buttonIconStyle} />}
        {!gradientLabel ? (
          <ButtonText textStroke={strokeColor} textStyle={textStyle}>
            {label}
          </ButtonText>
        ) : (
          <GradientLabel gradentLabelTextStyle={gradentLabelTextStyle} label={label} />
        )}
        {children}
      </ButtonContentWrapper>
    </ButtonWrapper>
  );
};

export default Button;

const ButtonWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['disabled', 'display', 'opacity', 'containerStyle'].includes(prop),
})`
  justify-content: center;
  align-items: center;
  display: flex;
  width: 180px;
  text-align: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: ${({ display }) => display};
  opacity: ${({ opacity }) => opacity};
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  ${({ containerStyle }) => containerStyle}
`;

const ButtonImage = styled.img
  .withConfig({
    shouldForwardProp: prop => !['imageSource', 'imageStyle', 'objectFit'].includes(prop),
  })
  .attrs(({ imageSource }) => ({
    src: imageSource,
  }))`
  position: absolute;
  width: 180px;
  object-fit: ${({ objectFit }) => objectFit || 'contain'};
  z-index: 1;
  ${({ imageStyle }) => imageStyle}
`;

const ButtonContentWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['hasChildren'].includes(prop),
})`
  align-items: center;
  display: flex;
  flex-direction: ${({ hasChildren }) => (hasChildren ? 'column' : 'row')};
`;

const ButtonIcon = styled.img.withConfig({
  shouldForwardProp: prop => !['buttonIconStyle'].includes(prop),
})`
  ${({ buttonIconStyle }) => buttonIconStyle};
  z-index: 1;
`;

const ButtonText = styled.p.withConfig({
  shouldForwardProp: prop => !['textStyle', 'textStroke'].includes(prop),
})`
  font-size: ${isTablet ? pwaStyleAdapter(isIPadMini ? 24 : 20, 'px', 1.5) : '24px'};
  font-weight: 700;
  margin: 0 auto !important;
  z-index: 2;
  color: ${theme.palette.common[1]};
  text-stroke: 1px ${({ textStroke }) => textStroke};
  -webkit-text-stroke: 1px ${({ textStroke }) => textStroke};
  ${({ theme }) => theme.Button.ButtonText}
  ${({ textStyle }) => textStyle}
`;
